import { ObjectRegistry } from '@lts-web/registry';
import ModuleTypes from '@lts-web/types/ModuleTypes';
import SearchModuleData, { conventions } from '@lts-web/data-objects/SearchModuleData';

class SearchModuleUtil {
    static #registry = new ObjectRegistry(SearchModuleData);
    static #supportedModules = [];
    static #calcModuleConvention = 'OMC';
    static #currModuleConvention = 'NMC';
    static currModuleNameGetter = () => null;

    /**
     * Get a specified module or the current module from the stored SearchModule instances
     * @param [moduleName] {String}
     * @returns {SearchModuleData|null}
     */
    static findSearchModule (moduleName) {
        if (typeof moduleName === 'string')
            return SearchModuleUtil.getModule(moduleName);

        return SearchModuleUtil.getCurrentModule();
    };

    /**
     * Create and register a new SearchModuleData instance
     * @param data {object}
     * @throws {Error} if SearchModule instance with the provided name already exists
     * @returns {boolean}
     */
    static createSearchModuleData (data) {
        if (typeof data !== 'object')
            throw new TypeError(`First argument must be of type 'object'`);

        const moduleName = data.name;

        if (SearchModuleUtil.hasModule(moduleName))
            throw new Error(`The module ${moduleName} already exists`);

        return SearchModuleUtil.#registry.set(moduleName, new SearchModuleData(data));
    }

    /**
     * Get a specified module from the stored SearchModule instances
     * @param moduleName {String}
     * @returns {SearchModuleData|null}
     */
    static getModule (moduleName) {
        return SearchModuleUtil.#registry.get(moduleName);
    }

    /**
     * Check if SearchModule instance exists by the provided name
     * @param moduleName
     * @returns {boolean}
     */
    static hasModule (moduleName) {
        return SearchModuleUtil.#registry.has(moduleName);
    }

    /**
     * Get current SearchModule
     * @returns {SearchModuleData|null}
     */
    static getCurrentModule () {
        const moduleName = SearchModuleUtil.getCurrentModuleName();
        return SearchModuleUtil.getModule(moduleName);
    }

    /**
     * Check if module name exists by provided convention
     *
     * @param moduleName
     * @param convention
     *
     * @returns {boolean}
     */
    static hasModuleName (moduleName, convention = SearchModuleUtil.#currModuleConvention) {
        convention = SearchModuleUtil.getModuleConvention(convention) || [];
        return convention.indexOf(moduleName) > -1;
    }

    /**
     * Get current module name
     * @param convertTo
     * @returns {String}
     */
    static getCurrentModuleName (convertTo = SearchModuleUtil.#currModuleConvention) {
        const moduleName = SearchModuleUtil.calculateModule();
        const convertForm = SearchModuleUtil.#calcModuleConvention;

        if (!conventions.hasOwnProperty(convertTo))
            throw new Error('Module convention id provided through `convertTo` argument does not exist!');

        return SearchModuleUtil.convertModule(moduleName, convertForm, convertTo);
    }

    /**
     * Calculate the current module
     * @returns {String}
     */
    static calculateModule () {
        return SearchModuleUtil.currModuleNameGetter();
    }

    /**
     * Get supported module names
     * @returns {Array}
     */
    static getSupportedModules () {
        return SearchModuleUtil.#supportedModules;
    }

    /**
     * Push supported module name
     * @param moduleName
     * @returns {boolean}
     */
    static pushSupportedModule (moduleName) {
        if (typeof moduleName !== 'string')
            return false;

        SearchModuleUtil.#supportedModules.push(moduleName);
        return true;
    }

    /**
     * Get a particular convention list
     * @param convention
     * @return {array}
     */
    static getModuleConvention (convention) {
        return conventions[convention];
    }

    /**
     * Module convention converter
     *
     * @param moduleName
     * @param convertFrom
     * @param convertTo
     *
     * @throws {Error} if convertFrom or convertTo are invalid module conventions
     * @returns {String|null}
     */
    static convertModule (moduleName, convertFrom, convertTo) {
        let index;
        if (!conventions.hasOwnProperty(convertFrom))
            throw new Error('Module convention id provided through `convertFrom` argument does not exist!');

        if (!conventions.hasOwnProperty(convertTo))
            throw new Error('Module convention id provided through `convertTo` argument does not exist!');

        index = conventions[convertFrom].indexOf(moduleName);
        return conventions[convertTo][index] || null;
    }

    /**
     * Check if moduleName is equal to property in ModuleTypes
     *
     * @param moduleName
     * @param moduleType
     * @param convention
     *
     * @return {boolean}
     */
    static equalModules (moduleName, moduleType, convention = 'NMC') {
        const moduleLMC = SearchModuleUtil.convertModule(moduleName, convention, 'LMC');

        if (!ModuleTypes.includes(moduleType)) {
            return false;
        }

        return moduleLMC === moduleType.description;
    }
}

export default SearchModuleUtil;